import axios from 'axios';
import type { DateTime } from 'luxon';
import { merge } from 'lodash';

/**
 *LabelsService
 */
export default class LabelsService
{
    public static path = 'admin/wk/labels';

    /**
     * @param filter FilterModel
     * @param pager Pager
     *
     * @returns Promise<Pagination<Resource<ListItemModel>>>
     */
    public static async getList(filter: FilterModel, pager: Pager): Promise<Pagination<Resource<ListItemModel>>>
    {
        return (await axios.get<Pagination<Resource<ListItemModel>>>(this.path, {
            params: merge({}, filter, pager.data())
        })).data;
    }

    /**
     * @returns Promise<ListItemModel[]>
     */
    public static async getOptions(): Promise<ListItemOptionsModel[]>
    {
        return (await axios.get<ListItemOptionsModel[]>(`${this.path}/options`)).data;
    }

    /**
     * @param id number
     *
     * @returns Promise<Resource<FormModel>>
     */
    public static async fetch(id: number): Promise<Resource<FormModel>>
    {
        return (await axios.get<Resource<FormModel>>(`${this.path}/${id}`)).data;
    }

    /**
     * @param model FormModel
     *
     * @returns Promise<Resource<FormModel>>
     */
    public static async create(model: FormModel): Promise<Resource<FormModel>>
    {
        return (await axios.post<Resource<FormModel>>(this.path, model)).data;
    }

    /**
     * @param id number
     * @param model FormModel
     *
     * @returns Promise<Statement>
     */
    public static async update(id: number, model: FormModel): Promise<Statement>
    {
        return (await axios.put<Statement>(`${this.path}/${id}`, model)).data;
    }

    /**
     * @param id number
     *
     * @returns Promise<Statement>
     */
    public static async remove(id: number): Promise<Statement>
    {
        return (await axios.delete(`${this.path}/${id}`)).data as Statement;
    }

    /**
     * @param search string
     * @param limit number
     *
     * @returns Promise<string[]>
     */
    public static async getAutocomplete(search: string, limit: number = 10, exept?: number[]): Promise<ListItemModel[]>
    {
        return (await axios.get<ListItemModel[]>(`${this.path}/autocomplete`, {
            params: {
                search: search,
                limit: limit,
                exept: exept
            }
        })).data;
    }

    /**
     * @param id number
     *
     * @returns Promise<ListItemModel>
     */
    public static async fetchAutocomplete(id: number): Promise<ListItemModel>
    {
        return (await axios.get<ListItemModel>(`${this.path}/autocomplete/${id}`)).data;
    }

    /**
     * @param id number
     *
     * @returns Promise<ListItemModel>
     */
    public static async getListByIds(ids: number[]): Promise<ListItemModel[]>
    {
        return (await axios.get<ListItemModel[]>(`${this.path}/ids`, {
            params: {
                ids: ids
            }
        })).data;
    }
}

export interface ListItemModel
{
    id: number;
    name: string;
    dateCreatedUtc: DateTime;
    dateModifiedUtc: DateTime;
}

export interface ListItemOptionsModel
{
    id: number;
    name: string;
}

export interface FormModel
{
    id: number;
    name: string;
    description: string;
}

export interface FilterModel
{
    name: string;
}
